/**
 * MarkAndDelete — standalone brush tool for marking and deleting mesh faces.
 */
import React, { useRef, useState, useCallback, useEffect } from 'react';
import {
    Box, Typography, Slider, Button, IconButton, Tooltip,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Undo as UndoIcon,
    CleaningServices,
} from '@mui/icons-material';
import * as THREE from 'three';
import { buildOverlayFromFaces, deleteFaces } from './meshOps';

const MAX_UNDO = 30;

export function useMarkAndDelete(props) {
    const {
        geometry, mesh, scene, camera, renderer, controls,
        onGeometryChange, active = false, onActiveChange,
    } = props;

    const [brushRadius, setBrushRadius] = useState(5);
    const [markedCount, setMarkedCount] = useState(0);

    const markedFacesRef = useRef(new Set());
    const overlayRef = useRef(null);
    const paintingRef = useRef(false);
    const unmarkRef = useRef(false);
    const brushSphereRef = useRef(null);
    const raycasterRef = useRef(new THREE.Raycaster());
    const mouseVecRef = useRef(new THREE.Vector2());

    const undoStackRef = useRef([]);
    const [undoCount, setUndoCount] = useState(0);

    useEffect(() => {
        if (!active) {
            if (brushSphereRef.current) {
                scene.remove(brushSphereRef.current);
                brushSphereRef.current.geometry.dispose();
                brushSphereRef.current.material.dispose();
                brushSphereRef.current = null;
            }
            return;
        }
        const geo = new THREE.SphereGeometry(1, 24, 12);
        const mat = new THREE.MeshBasicMaterial({
            color: '#ff9100', transparent: true, opacity: 0.2,
            depthWrite: false, side: THREE.DoubleSide,
        });
        const sphere = new THREE.Mesh(geo, mat);
        sphere.visible = false;
        sphere.renderOrder = 999;
        scene.add(sphere);
        brushSphereRef.current = sphere;
        return () => {
            scene.remove(sphere);
            geo.dispose();
            mat.dispose();
            brushSphereRef.current = null;
        };
    }, [active, scene]);

    const updateOverlay = useCallback(() => {
        if (overlayRef.current) {
            overlayRef.current.removeFromParent();
            overlayRef.current.geometry.dispose();
            overlayRef.current.material.dispose();
            overlayRef.current = null;
        }
        if (markedFacesRef.current.size === 0) return;
        const geo = buildOverlayFromFaces(geometry, markedFacesRef.current);
        const mat = new THREE.MeshBasicMaterial({
            color: '#ff1744', transparent: true, opacity: 0.35,
            side: THREE.DoubleSide, depthTest: true, depthWrite: false,
            polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1,
        });
        const overlay = new THREE.Mesh(geo, mat);
        overlay.renderOrder = 998;
        mesh.add(overlay);
        overlayRef.current = overlay;
    }, [geometry, mesh]);

    const paintAtPoint = useCallback((clientX, clientY) => {
        const rect = renderer.domElement.getBoundingClientRect();
        mouseVecRef.current.set(
            ((clientX - rect.left) / rect.width) * 2 - 1,
            -((clientY - rect.top) / rect.height) * 2 + 1,
        );
        raycasterRef.current.setFromCamera(mouseVecRef.current, camera);

        const intersects = raycasterRef.current.intersectObject(mesh);
        if (intersects.length === 0) return;

        const hit = intersects[0];
        const pos = geometry.getAttribute('position');
        const idx = geometry.getIndex();
        const fCount = idx ? idx.count / 3 : pos.count / 3;
        const r2 = brushRadius * brushRadius;
        const unmark = unmarkRef.current;
        const faceSet = markedFacesRef.current;
        let changed = false;

        for (let f = 0; f < fCount; f++) {
            let i0, i1, i2;
            if (idx) {
                i0 = idx.getX(f * 3); i1 = idx.getX(f * 3 + 1); i2 = idx.getX(f * 3 + 2);
            } else {
                i0 = f * 3; i1 = f * 3 + 1; i2 = f * 3 + 2;
            }
            const cx = (pos.getX(i0) + pos.getX(i1) + pos.getX(i2)) / 3;
            const cy = (pos.getY(i0) + pos.getY(i1) + pos.getY(i2)) / 3;
            const cz = (pos.getZ(i0) + pos.getZ(i1) + pos.getZ(i2)) / 3;

            const dx = cx - hit.point.x;
            const dy = cy - hit.point.y;
            const dz = cz - hit.point.z;
            if (dx * dx + dy * dy + dz * dz > r2) continue;

            if (unmark) {
                if (faceSet.delete(f)) changed = true;
            } else {
                if (!faceSet.has(f)) { faceSet.add(f); changed = true; }
            }
        }

        if (changed) {
            updateOverlay();
            setMarkedCount(markedFacesRef.current.size);
        }
    }, [geometry, mesh, camera, renderer, brushRadius, updateOverlay]);

    const handlePointerDown = useCallback((e) => {
        if (!active) return;
        unmarkRef.current = e.button === 2 || e.ctrlKey;
        paintingRef.current = true;
        if (controls) controls.enabled = false;
        paintAtPoint(e.clientX, e.clientY);
    }, [active, paintAtPoint, controls]);

    const handlePointerMove = useCallback((e) => {
        if (!active) return;

        const rect = renderer.domElement.getBoundingClientRect();
        mouseVecRef.current.set(
            ((e.clientX - rect.left) / rect.width) * 2 - 1,
            -((e.clientY - rect.top) / rect.height) * 2 + 1,
        );
        raycasterRef.current.setFromCamera(mouseVecRef.current, camera);
        const intersects = raycasterRef.current.intersectObject(mesh);
        if (brushSphereRef.current) {
            if (intersects.length > 0) {
                brushSphereRef.current.position.copy(intersects[0].point);
                brushSphereRef.current.scale.setScalar(brushRadius);
                brushSphereRef.current.visible = true;
            } else {
                brushSphereRef.current.visible = false;
            }
        }

        if (!paintingRef.current) return;
        paintAtPoint(e.clientX, e.clientY);
    }, [active, mesh, camera, renderer, brushRadius, paintAtPoint]);

    const handlePointerUp = useCallback(() => {
        if (!active) return;
        paintingRef.current = false;
        if (controls) controls.enabled = true;
    }, [active, controls]);

    const handleContextMenu = useCallback((e) => {
        if (active) e.preventDefault();
    }, [active]);

    const handleDelete = useCallback(() => {
        if (markedFacesRef.current.size === 0) return;
        undoStackRef.current.push(geometry.clone());
        if (undoStackRef.current.length > MAX_UNDO) undoStackRef.current.shift();
        setUndoCount(undoStackRef.current.length);

        const newGeo = deleteFaces(geometry, markedFacesRef.current);

        if (overlayRef.current) {
            overlayRef.current.removeFromParent();
            overlayRef.current.geometry.dispose();
            overlayRef.current.material.dispose();
            overlayRef.current = null;
        }
        markedFacesRef.current.clear();
        setMarkedCount(0);

        onGeometryChange(newGeo);
    }, [geometry, onGeometryChange]);

    const handleUndo = useCallback(() => {
        const prev = undoStackRef.current.pop();
        if (!prev) return;
        setUndoCount(undoStackRef.current.length);
        onGeometryChange(prev);
    }, [onGeometryChange]);

    const handleClear = useCallback(() => {
        if (overlayRef.current) {
            overlayRef.current.removeFromParent();
            overlayRef.current.geometry.dispose();
            overlayRef.current.material.dispose();
            overlayRef.current = null;
        }
        markedFacesRef.current.clear();
        setMarkedCount(0);
    }, []);

    useEffect(() => {
        if (!active) handleClear();
    }, [active, handleClear]);

    const Panel = useCallback(() => {
        if (!active) return null;
        return (
            <Box sx={{ minWidth: 140 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.6rem', fontWeight: 600, width: 44, flexShrink: 0 }}>Radius</Typography>
                    <Slider
                        size="small"
                        value={brushRadius}
                        onChange={(_, v) => setBrushRadius(v)}
                        min={1} max={30} step={0.5}
                        sx={{ color: '#ff9100', '& .MuiSlider-thumb': { width: 12, height: 12 } }}
                    />
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.6rem', width: 32, textAlign: 'right', flexShrink: 0 }}>{brushRadius}mm</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    <Button
                        size="small"
                        variant="contained"
                        disabled={markedCount === 0}
                        onClick={handleDelete}
                        startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                        sx={{
                            flex: 1, bgcolor: '#ff1744', fontSize: '0.65rem', py: '3px', textTransform: 'none',
                            '&:hover': { bgcolor: '#d50000' },
                            '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.2)' },
                        }}
                    >
                        Delete ({markedCount})
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={markedCount === 0}
                        onClick={handleClear}
                        sx={{
                            color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.15)', fontSize: '0.65rem', py: '3px', textTransform: 'none',
                            '&:hover': { borderColor: 'rgba(255,255,255,0.3)', bgcolor: 'rgba(255,255,255,0.05)' },
                            '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)', borderColor: 'rgba(255,255,255,0.08)' },
                        }}
                    >
                        Clear
                    </Button>
                    <Tooltip title="Undo last delete" placement="top" arrow>
                        <span>
                            <IconButton
                                size="small"
                                disabled={undoCount === 0}
                                onClick={handleUndo}
                                sx={{
                                    color: undoCount > 0 ? '#ffa726' : 'rgba(255,255,255,0.15)',
                                    border: '1px solid',
                                    borderColor: undoCount > 0 ? 'rgba(255,167,38,0.3)' : 'rgba(255,255,255,0.08)',
                                    borderRadius: '6px',
                                    px: 0.8,
                                    '&:hover': { bgcolor: 'rgba(255,167,38,0.1)' },
                                }}
                            >
                                <UndoIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.55rem', mt: 0.5 }}>
                    Left-click: mark | Right-click: unmark
                </Typography>
            </Box>
        );
    }, [active, brushRadius, markedCount, undoCount, handleDelete, handleClear, handleUndo]);

    return {
        handlePointerDown,
        handlePointerMove,
        handlePointerUp,
        handleContextMenu,
        active,
        Panel,
    };
}

export const MarkAndDeleteButton = ({ active, disabled, onClick }) => (
    <Tooltip title="Mark & Delete" placement="top" arrow>
        <span>
            <IconButton
                disabled={disabled}
                onClick={onClick}
                sx={{
                    color: '#ff9100',
                    bgcolor: active ? 'rgba(255,145,0,0.15)' : undefined,
                    '&:hover': { bgcolor: 'rgba(255,145,0,0.15)' },
                }}
            >
                <CleaningServices sx={{ fontSize: 22 }} />
            </IconButton>
        </span>
    </Tooltip>
);
